import React from 'react';
import { Box, Container, Typography, Button, Grid, IconButton } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useLocation } from 'react-router-dom';
import { scroller } from 'react-scroll';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const goToContact = () => {
    if (location.pathname === '/') {
      scroller.scrollTo('contact', { smooth: true, duration: 600, offset: -80 });
    } else {
      navigate('/');
      setTimeout(() => scroller.scrollTo('contact', { smooth: true, duration: 600, offset: -80 }), 300);
    }
  };

  const goToSection = (section) => {
    if (location.pathname === '/') {
      scroller.scrollTo(section, { smooth: true, duration: 600, offset: -80 });
    } else {
      navigate('/');
      setTimeout(() => scroller.scrollTo(section, { smooth: true, duration: 600, offset: -80 }), 300);
    }
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
      {/* CTA band */}
      <Box sx={{
        py: { xs: 7, md: 10 },
        bgcolor: '#F8F9FF',
        borderBottom: '1px solid #E5E7EB',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, height: 300, background: 'radial-gradient(ellipse, rgba(99,102,241,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 4 } }}>
          <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' }, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 2 }}>
            Ready to ditch the spreadsheets?
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: { xs: '0.95rem', md: '1rem' }, mb: 4, lineHeight: 1.8 }}>
            Let's talk about what your business needs and how we can help.
          </Typography>
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={goToContact} sx={{
            bgcolor: '#6366F1', color: '#fff',
            px: { xs: 3, md: 5 }, py: 1.6,
            fontWeight: 700, fontSize: '1rem', borderRadius: '8px', textTransform: 'none',
            boxShadow: '0 4px 20px rgba(99,102,241,0.3)',
            '&:hover': { bgcolor: '#5254CC', transform: 'translateY(-2px)', boxShadow: '0 8px 28px rgba(99,102,241,0.4)' },
            transition: 'all 0.25s',
          }}>
            Start the Conversation
          </Button>
        </Container>
      </Box>

      {/* Links */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 4, md: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'block', textAlign: { xs: 'center', sm: 'left' } }}>
              <img src="/new-logo.png" alt="InfyniHub" style={{ height: '113px', objectFit: 'contain', marginBottom: '14px' }} />
            </Box>
            <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.8, mb: 3, maxWidth: 260, textAlign: { xs: 'center', sm: 'left' }, mx: { xs: 'auto', sm: 0 } }}>
              Infinite Solutions & Services. One Hub.
            </Typography>
            <IconButton
              href="https://www.linkedin.com/in/infynihub-com-aa29633ab/"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ color: '#9CA3AF', '&:hover': { color: '#6366F1', bgcolor: '#EEF2FF' }, transition: 'all 0.2s' }}
            >
              <LinkedInIcon />
            </IconButton>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Products</Typography>
            {[{ label: 'GearDesk', path: '/geardesk' }, { label: 'LineDesk', path: '/linedesk' }].map((item) => (
              <Typography key={item.label} onClick={() => navigate(item.path)} sx={{ color: '#6B7280', mb: 1.5, fontSize: '0.88rem', cursor: 'pointer', '&:hover': { color: '#6366F1' }, transition: 'color 0.2s' }}>
                {item.label}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={6} sm={3} md={3}>
            <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Services</Typography>
            {['IT Consulting', 'Website Development', 'DevOps Support', 'Software Licensing', 'Hardware Sourcing'].map((item) => (
              <Typography key={item} onClick={() => goToSection('services')} sx={{ color: '#6B7280', mb: 1.5, fontSize: '0.88rem', cursor: 'pointer', '&:hover': { color: '#6366F1' }, transition: 'color 0.2s' }}>
                {item}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Company</Typography>
            <Typography onClick={() => navigate('/blogs')} sx={{ color: '#6B7280', mb: 1.5, fontSize: '0.88rem', cursor: 'pointer', '&:hover': { color: '#6366F1' }, transition: 'color 0.2s' }}>Blogs</Typography>
            <Typography onClick={() => navigate('/privacy-policy')} sx={{ color: '#6B7280', mb: 1.5, fontSize: '0.88rem', cursor: 'pointer', '&:hover': { color: '#6366F1' }, transition: 'color 0.2s' }}>Privacy Policy</Typography>
            <Typography
              component="a"
              href="mailto:admin@infynihub.com"
              sx={{ color: '#6B7280', fontSize: '0.88rem', mt: 1, display: 'block', textDecoration: 'none', '&:hover': { color: '#6366F1' }, transition: 'color 0.2s' }}
            >
              admin@infynihub.com
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ borderTop: '1px solid #F3F4F6', mt: 6, pt: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>© {new Date().getFullYear()} InfyniHub. All rights reserved.</Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Built for SMEs. Delivered end-to-end.</Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
